/**
 * <SlotChangeCoursesStep> — l'étape « Quel cours ne convient plus ? » de « Modifier mes créneaux »
 * (organism).
 *
 * Les cours de la semaine de l'enfant, une carte d'option chacun — le visage du tuteur, « Jeudi ·
 * 18h00 », et la ligne d'une séance (« Maths · avec Camille · 1h », `sessionDetailsLabel`) —, qu'on
 * coche un ou plusieurs. Dessous, ce que le changement ne promet pas (garder ses tuteurs), et
 * l'autre geste pour une absence d'une fois, centré : « décalez la séance » est un lien quand
 * l'écran sait ouvrir « Décaler une séance » (`onMoveSession`), la phrase reste entière sinon.
 *
 * Contrôlé : la sélection arrive, un cours repart par `onToggle`. Pendant que les cours arrivent,
 * leurs cartes montrent leur forme.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Stack } from "../../atoms/containers/Stack"
import { Avatar } from "../../atoms/Avatar"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { OptionCard } from "../../molecules/OptionCard"
import { OptionCardSkeleton } from "../../molecules/OptionCard/OptionCardSkeleton"
import { type UISlotChangeCourse } from "../../../ports/primary/ui/types"
import { sessionDetailsLabel, tutorShortName } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { formatWeeklySlot } from "../../../ports/primary/ui/formatters/weeklySlotFormatter"

/** Le visage du tuteur sur ses cours. */
const LEADING_SIZE = 44

const NO_GUARANTEE = "Changer d'horaire ne garantit pas de garder ses tuteurs actuels sur les nouveaux horaires."
const ONE_OFF_ABSENCE = "Pour une absence ponctuelle, "
const MOVE_SESSION = "décalez la séance"
const TUTOR_CHANGE = "Pour changer de tuteur, "
const WRITE_TO_US = "écrivez-nous"

export type SlotChangeCoursesStepProps = {
    courses: UISlotChangeCourse[]
    selectedIds: number[]
    onToggle: (liveSessionId: number) => void
    loading?: boolean
    /** Ouvre « Décaler une séance ». Absent : la phrase s'écrit sans lien. */
    onMoveSession?: () => void
    /** Ouvre la messagerie, le message déjà préparé. Absent : pas de phrase. */
    onAskTutorChange?: () => void
    testID?: string
}

/** « Maths · avec Camille · 1h » — sans tuteur, la ligne commence à la matière et finit à la durée. */
function courseDetails(course: UISlotChangeCourse): string {
    if (course.tutor != null) {
        return sessionDetailsLabel({ subjects: course.subjects, tutor: course.tutor, duration: course.duration })
    }
    return sessionDetailsLabel(
        { subjects: course.subjects, tutor: { id: 0, firstname: "", lastname: "" }, duration: course.duration },
        { tutor: false }
    )
}

export function SlotChangeCoursesStep(props: SlotChangeCoursesStepProps) {
    const { courses, selectedIds, onToggle, loading = false, onMoveSession, onAskTutorChange, testID } = props

    const list = loading ? (
        <DelayedSkeleton accessibilityLabel="Chargement des cours">
            <Stack gap="2">
                <OptionCardSkeleton leadingSize={LEADING_SIZE} labelWidth={120} descriptionWidth={160} />
                <OptionCardSkeleton leadingSize={LEADING_SIZE} labelWidth={110} descriptionWidth={140} />
            </Stack>
        </DelayedSkeleton>
    ) : (
        <Stack gap="2">
            {courses.map((course) => (
                <OptionCard
                    key={course.liveSessionId}
                    label={formatWeeklySlot(course.label, course.dayTime)}
                    description={courseDetails(course)}
                    selected={selectedIds.includes(course.liveSessionId)}
                    onPress={() => onToggle(course.liveSessionId)}
                    leading={
                        course.tutor != null ? (
                            <Avatar
                                uri={course.tutor.avatarUrl}
                                name={tutorShortName(course.tutor)}
                                size={LEADING_SIZE}
                                signed
                            />
                        ) : (
                            <IconBubble iconName="calendar-alt" size={LEADING_SIZE} />
                        )
                    }
                />
            ))}
        </Stack>
    )

    return (
        <Stack gap="4" testID={testID}>
            {list}
            {/* Un avis, pas une alarme : un aplat ambre clair, l'encre de l'avertissement, sans filet ni glyphe. */}
            <View style={styles.notice}>
                <Text size="text-sm" color="warning-strong">
                    {NO_GUARANTEE}
                </Text>
            </View>
            <Text size="text-sm" color="text-muted" center>
                {ONE_OFF_ABSENCE}
                {onMoveSession != null ? (
                    <Text
                        size="text-sm"
                        weight="semibold"
                        color="role"
                        onPress={onMoveSession}
                        accessibilityRole="link"
                        style={styles.link}
                    >
                        {MOVE_SESSION}
                    </Text>
                ) : (
                    MOVE_SESSION
                )}
                .
            </Text>
            {onAskTutorChange != null && (
                <Text size="text-sm" color="text-muted" center>
                    {TUTOR_CHANGE}
                    <Text
                        size="text-sm"
                        weight="semibold"
                        color="role"
                        onPress={onAskTutorChange}
                        accessibilityRole="link"
                        style={styles.link}
                    >
                        {WRITE_TO_US}
                    </Text>
                    .
                </Text>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    link: { textDecorationLine: "underline" as const },
    notice: {
        backgroundColor: theme.colors.warningLight,
        borderRadius: theme.radius.md,
        paddingHorizontal: theme.spacing["3"],
        paddingVertical: theme.spacing["3"],
    },
}))
